import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "auditButton", "status", "progress", "auditResults"]

  static values = {
    triggerUrl: String,
    statusUrl: String,
    auditUrl: String,
    pollingInterval: { type: Number, default: 2000 }
  }

  connect() {
    this.pollTimer = null
    this.userTriggered = false
    this.checkStatus()
  }

  disconnect() {
    this.stopPolling()
  }

  async audit() {
    this.auditButtonTarget.disabled = true
    this.auditResultsTarget.innerHTML = '<div class="text-info mt-2"><i class="fa fa-spinner fa-spin"></i> Auditing welder mentions across all reports...</div>'

    try {
      const response = await fetch(this.auditUrlValue, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      if (!response.ok) {
        this.auditResultsTarget.innerHTML = `<div class="alert alert-danger mt-2">${data.error || 'Audit failed'}</div>`
        this.auditButtonTarget.disabled = false
        return
      }

      this.renderAuditResults(data)
      this.auditButtonTarget.disabled = false
    } catch (error) {
      this.auditResultsTarget.innerHTML = `<div class="alert alert-danger mt-2">Error: ${error.message}</div>`
      this.auditButtonTarget.disabled = false
    }
  }

  renderAuditResults(data) {
    if (data.problem_count === 0) {
      this.auditResultsTarget.innerHTML = `
        <div class="alert alert-success mt-2 mb-0">
          <i class="fa fa-check-circle"></i>
          <strong>All clear!</strong> ${data.total_reports} reports checked — all welder mentions are correct.
        </div>`
      return
    }

    let html = `
      <div class="alert alert-warning mt-2">
        <i class="fa fa-exclamation-triangle"></i>
        <strong>${data.problem_count} of ${data.total_reports} reports</strong> have issues.
        ${data.correct_count} reports are correct.
      </div>
      <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
        <table class="table table-sm table-bordered mb-0">
          <thead class="thead-light">
            <tr>
              <th>Report</th>
              <th>Type</th>
              <th>Task</th>
              <th>Issues</th>
            </tr>
          </thead>
          <tbody>`

    data.problems.forEach(p => {
      let issuesBadges = []

      if (p.orphans && p.orphans.length > 0) {
        issuesBadges.push(`<span class="badge badge-dark mr-1">${p.orphans.length} orphaned (deleted welder)</span>`)
      }
      if (p.duplicates && p.duplicates.length > 0) {
        issuesBadges.push(`<span class="badge badge-secondary mr-1">${p.duplicates.length} duplicate</span>`)
      }
      if (p.missing) {
        p.missing.forEach(w => {
          issuesBadges.push(`<span class="badge badge-danger mr-1">Missing: ${this.escapeHtml(w.name)} (${this.escapeHtml(w.welder_id)})</span>`)
        })
      }
      if (p.stale) {
        p.stale.forEach(w => {
          issuesBadges.push(`<span class="badge badge-warning mr-1">Stale: ${this.escapeHtml(w.name)} (${this.escapeHtml(w.welder_id)})</span>`)
        })
      }

      html += `
            <tr>
              <td>${this.escapeHtml(p.report_number || `#${p.report_id}`)}</td>
              <td><span class="badge badge-info">${this.escapeHtml(p.test_method)}</span></td>
              <td>${p.task_id}</td>
              <td>${issuesBadges.join(' ')}</td>
            </tr>`
    })

    html += `
          </tbody>
        </table>
      </div>`

    this.auditResultsTarget.innerHTML = html
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = String(str)
    return div.innerHTML
  }

  async trigger() {
    this.userTriggered = true
    this.buttonTarget.disabled = true
    this.statusTarget.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Starting...'
    this.statusTarget.className = 'text-info mt-2'

    try {
      const response = await fetch(this.triggerUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.status === 409) {
        this.statusTarget.textContent = 'A reconciliation is already in progress...'
        this.statusTarget.className = 'text-warning mt-2'
        this.startPolling()
        return
      }

      if (!response.ok) {
        this.statusTarget.textContent = data.message || 'Failed to start reconciliation'
        this.statusTarget.className = 'text-danger mt-2'
        this.buttonTarget.disabled = false
        return
      }

      this.startPolling()
    } catch (error) {
      this.statusTarget.textContent = `Error: ${error.message}`
      this.statusTarget.className = 'text-danger mt-2'
      this.buttonTarget.disabled = false
    }
  }

  startPolling() {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => this.checkStatus(), this.pollingIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async checkStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      switch (data.status) {
        case 'running':
          this.buttonTarget.disabled = true
          this.showProgress(data)
          if (!this.pollTimer) this.startPolling()
          break
        case 'completed':
          this.stopPolling()
          if (this.userTriggered) {
            this.showCompleted(data)
          }
          this.buttonTarget.disabled = false
          break
        case 'error':
          this.stopPolling()
          this.statusTarget.textContent = `Error: ${data.message}`
          this.statusTarget.className = 'text-danger mt-2'
          this.progressTarget.classList.add('d-none')
          this.buttonTarget.disabled = false
          break
        case 'idle':
        default:
          this.stopPolling()
          this.statusTarget.textContent = ''
          this.progressTarget.classList.add('d-none')
          this.buttonTarget.disabled = false
          break
      }
    } catch (error) {
      console.error('Status check failed:', error)
    }
  }

  showProgress(data) {
    const percent = data.total > 0 ? Math.round((data.current / data.total) * 100) : 0
    this.statusTarget.innerHTML = `<i class="fa fa-spinner fa-spin"></i> Fixing welder mentions... Report ${data.current}/${data.total}`
    this.statusTarget.className = 'text-info mt-2'

    this.progressTarget.classList.remove('d-none')
    const bar = this.progressTarget.querySelector('.progress-bar')
    if (bar) {
      bar.style.width = `${percent}%`
      bar.setAttribute('aria-valuenow', percent)
      bar.textContent = `${percent}%`
    }
  }

  showCompleted(data) {
    let parts = [`Mentions created: ${data.created}`, `Stale mentions removed: ${data.removed}`, `Orphans removed: ${data.orphans_removed || 0}`, `Duplicates removed: ${data.duplicates_removed || 0}`, `Unchanged: ${data.unchanged}`]
    this.statusTarget.innerHTML = `<i class="fa fa-check-circle"></i> Welder mentions fixed across ${data.total} reports! ${parts.join(', ')}`
    this.statusTarget.className = 'text-success mt-2'

    this.progressTarget.classList.remove('d-none')
    const bar = this.progressTarget.querySelector('.progress-bar')
    if (bar) {
      bar.style.width = '100%'
      bar.setAttribute('aria-valuenow', 100)
      bar.textContent = '100%'
      bar.classList.remove('progress-bar-animated', 'progress-bar-striped')
      bar.classList.add('bg-success')
    }
  }
}
